import { ArrowUpRight, Plus, Star } from "lucide-react";
import { Reveal } from "@/components/pdp/reveal";
import { useProduct } from "@/components/pdp/PdpDataContext";

export const CompleteRoutine = () => {
  const P = useProduct();
  const { routine } = P;
  const wide = !!P.routineWideBanner;
  return (
    <section className="bg-white py-32" data-testid="complete-routine">
      <div className={wide ? "w-full px-4 lg:px-6" : "max-w-[1600px] mx-auto px-4 lg:px-8"}>
        <div className={`grid grid-cols-1 lg:grid-cols-12 ${wide ? "gap-8 lg:gap-6" : "gap-8"}`}>
          {/* Sticky lifestyle banner — fills viewport height while products scroll */}
          <div className={`${wide ? "lg:col-span-8" : "lg:col-span-5"} h-full`}>
            <div className="sticky top-24">
              <div className={`relative editorial-frame group bg-riversol-sand overflow-hidden ${wide ? "h-[calc(100vh-6rem)]" : "h-[calc(100vh-8rem)]"}`}>
                <img
                  src={routine.lifestyleImage}
                  alt={routine.lifestyleAlt}
                  className="w-full h-full object-cover img-zoom absolute inset-0"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/15 to-transparent" />
                <div className="absolute bottom-12 left-12 right-12 text-white">
                  <div className="pdp-eyebrow font-mono text-[11px] tracking-[0.24em] uppercase text-white/70 mb-4">
                    {routine.eyebrow}
                  </div>
                  <h2 className={`leading-[1.03] tracking-tight font-semibold ${wide ? "text-5xl lg:text-6xl" : "text-4xl lg:text-5xl"} max-w-lg`}>
                    {routine.title}
                  </h2>
                </div>
              </div>
            </div>
          </div>

          {/* Products column */}
          {wide ? (
            /* V3: vertical product cards (image on top, details below — like "You may also love") in a 2-column grid */
            <div className="lg:col-span-4 flex flex-col">
              <div className="mb-6 px-1 flex items-baseline justify-between gap-4">
                <div className="font-mono text-[11px] tracking-[0.24em] uppercase text-neutral-500">
                  Complete your selection
                </div>
                <div className="font-mono text-[10px] tracking-[0.14em] uppercase text-neutral-400">
                  {routine.products.length} products
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {routine.products.map((p, i) => (
                  <Reveal key={i} delay={i * 0.06}>
                    <article
                      data-testid={`complementary-card-${i}`}
                      className="group bg-white border border-neutral-200 flex flex-col hover:border-riversol-blue transition-colors duration-300 h-full"
                    >
                      <div className="relative aspect-square bg-riversol-sand overflow-hidden border-b border-neutral-200">
                        {p.image ? (
                          <img src={p.image} alt={p.name} className="w-full h-full object-cover img-zoom" />
                        ) : (
                          <span className="absolute inset-0 flex items-center justify-center font-mono text-[10px] tracking-[0.18em] uppercase text-neutral-400 text-center px-4 leading-relaxed">
                            {p.name}
                          </span>
                        )}
                        <button
                          data-testid={`complementary-add-${i}`}
                          aria-label={`Add ${p.name}`}
                          className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-white/95 backdrop-blur flex items-center justify-center text-neutral-900 hover:bg-riversol-blue hover:text-white transition-colors shadow-sm"
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                      <div className="p-5 flex flex-col flex-1 gap-3">
                        <h3 className="text-[15px] text-neutral-900 leading-snug">{p.name}</h3>
                        <p className="text-[12px] text-neutral-500 leading-snug line-clamp-3">{p.reason}</p>
                        <div className="mt-auto flex items-center justify-between pt-3 border-t border-neutral-100">
                          <span className="font-mono text-sm text-neutral-900">{p.price}</span>
                          <button
                            data-testid={`complementary-shop-${i}`}
                            className="group/cta inline-flex items-center gap-1.5 bg-riversol-blue text-white font-mono text-[10px] tracking-[0.18em] uppercase px-4 py-2 rounded-full hover:bg-neutral-900 transition-colors"
                          >
                            {P.v31 ? "Add" : "Shop now"}
                            <ArrowUpRight size={12} className="group-hover/cta:translate-x-0.5 group-hover/cta:-translate-y-0.5 transition-transform" />
                          </button>
                        </div>
                      </div>
                    </article>
                  </Reveal>
                ))}
              </div>
            </div>
          ) : (
            /* V2: horizontal card layout, stacked */
            <div className="lg:col-span-7 flex flex-col gap-6">
              {routine.products.map((p, i) => (
                <Reveal key={i} delay={i * 0.08}>
                  <div className="group border border-neutral-200 p-6 flex gap-6 items-stretch hover:border-riversol-blue transition-colors duration-300">
                    <div className="w-40 h-40 shrink-0 bg-riversol-sand editorial-frame flex items-center justify-center border border-neutral-200 overflow-hidden">
                      {p.image ? (
                        <img src={p.image} alt={p.name} className="w-full h-full object-cover img-zoom" />
                      ) : (
                        <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-neutral-400 text-center px-4 leading-relaxed">
                          Product image<br />{p.name}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 flex flex-col">
                      <h3 className="text-xl text-neutral-900 leading-snug mb-2">{p.name}</h3>
                      <p className="text-[14px] text-neutral-500 leading-relaxed flex-1 max-w-lg">{p.reason}</p>
                      <div className="flex items-center justify-between mt-5 pt-5 border-t border-neutral-200">
                        <span className="font-mono text-sm text-neutral-900">{p.price}</span>
                        <button
                          data-testid={`complementary-add-${i}`}
                          className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.12em] text-riversol-blue hover:gap-2.5 transition-all"
                        >
                          Quick add <Plus size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export const FinalConversion = ({ variant }) => {
  const P = useProduct();
  const v = P.variants.find((x) => x.id === variant);
  const imgClass = P.final.imageMode === "cover" ? "object-cover" : "object-contain p-16";
  return (
    <section className="bg-riversol-blue text-white py-32" data-testid="final-conversion" data-inverted-bg="blue">
      <div className="max-w-[1600px] mx-auto px-8 grid grid-cols-12 gap-16 items-center">
        <div className="col-span-6 relative">
          <div className="editorial-frame aspect-[4/3] bg-white/5 group">
            <img src={P.final.image} alt={P.final.imageAlt} className={`w-full h-full img-zoom ${imgClass}`} />
          </div>
        </div>
        <div className="col-span-6">
          <div className="pdp-eyebrow font-mono text-[11px] tracking-[0.24em] uppercase text-white/60 mb-6">
            {P.final.eyebrow}
          </div>
          <h2 className="text-4xl lg:text-[3.25rem] leading-[1.05] tracking-tight font-semibold mb-6">
            {P.final.titleLines.map((l, i) => <span key={i} className="block">{l}</span>)}
          </h2>
          <div className="flex items-center gap-3 mb-8">
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => <Star key={i} size={15} className={i < Math.round(P.rating) ? "fill-white text-white" : "text-white/30"} />)}
            </div>
            <span className="text-sm text-white/70">{P.rating} · {P.reviewCount.toLocaleString()} reviews</span>
          </div>
          <div className="flex items-end gap-4 mb-2">
            <span className="font-mono text-4xl">{P.price.regular}</span>
            <span className="text-sm text-white/50 mb-1.5">/ {P.price.member} with Riversol+</span>
          </div>
          <div className="font-mono text-xs text-white/60 mb-10">Selected — {v?.label}</div>
          <div className="flex items-center gap-6 flex-wrap">
            <button
              data-testid="final-shop-now"
              className="group flex items-center gap-3 bg-white text-riversol-blue text-sm tracking-[0.12em] uppercase font-medium px-10 py-4 hover:bg-riversol-sand transition-colors"
            >
              {P.ctaLabel} <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </button>
            <span className="text-sm text-white/60">{P.shipping} · {P.returns}</span>
          </div>
        </div>
      </div>
    </section>
  );
};
